import { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";

import Builder from "containers/admin/clients/touchpoint/components/builder/builder";
import LPBuilderDesignTab from "containers/admin/clients/touchpoint/components/lp-builder/lp-builder-design-tab/lp-builder-design-tab";
import LPBuilderContentSettingsTab from "containers/admin/clients/touchpoint/components/lp-builder/lp-builder-content-settings-tab/lp-builder-content-settings-tab";
import LPBuilderPreviewTab from "containers/admin/clients/touchpoint/components/lp-builder/lp-builder-preview-tab/lp-builder-preview-tab";
import {
  BuilderTabNav,
  BuilderTabId,
} from "containers/admin/clients/touchpoint/components/builder/builder-tab-nav/builder-tab-nav";
import { useSaveTouchpoint } from "containers/admin/clients/touchpoint/components/builder/use-save-touchpoint";
import useLPSettingsFields from "containers/admin/clients/touchpoint/components/lp-builder/use-lp-builder-form-fields";

import { Content, ContentWrapper } from "components/partials/layout/layout";
import { LockReleaseModal } from "components/modals/lock-release-modal/lock-release-modal";

import { useBuilderContent } from "hooks/api/use-touchpoint-versions";
import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import type { LandingPageAttributes } from "hooks/api/use-touchpoint-versions";

import { usePagePermission } from "hooks/use-page-permission";

import { addAnnotationIdToElements } from "utilities";
import { BLANK_BEE_TEMPLATE, PROGRAM_TEMPLATES_CAMPAIGN_NAME } from "utilities/constants";

import { Permission } from "types/auth";
import { TouchpointType } from "types/touchpoint";
import { LoadingSpinner } from "components/partials/loading-screen/loading-screen";

interface LPBuilderProps {
  previewMode: boolean;
}
/**
 * Extends Builder.tsx
 */
const LPBuilder = ({ previewMode }: LPBuilderProps) => {
  usePagePermission(Permission.PERM_TOUCHPOINT_READ);

  const touchpointContent = useBuilderContent() as LandingPageAttributes;
  const currentTouchpoint = useCurrentTouchpoint();
  const currentCampaign = useCurrentCampaign();
  const isTemplate = currentCampaign.name === PROGRAM_TEMPLATES_CAMPAIGN_NAME;
  const touchpointIsOther = touchpointContent.type === TouchpointType.OTHER;
  const hiddenTabIds: BuilderTabId[] | undefined = touchpointIsOther
    ? ["content-settings"]
    : undefined;

  const formFields = useLPSettingsFields({ isTemplate });
  const [isNewTouchpoint, setIsNewTouchpoint] = useState(false);
  const { saveTouchpoint, isLockReleaseModalOpen, setIsLockReleaseModalOpen } = useSaveTouchpoint();
  const [isSaved, setIsSaved] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [tabValidity, setTabValidity] = useState<Record<BuilderTabId, boolean>>({
    design: true,
    "content-settings": isTemplate || !!touchpointContent.publishable,
    preview: true,
  });

  useEffect(() => {
    setTabValidity((prev) => ({
      ...prev,
      "content-settings": isTemplate || !!touchpointContent.publishable,
    }));
  }, [touchpointContent.publishable, isTemplate]);

  const navigate = useNavigate();

  const handleLockRelease = () => {
    setIsLockReleaseModalOpen(false);
    navigate("../../../preview", { state: location.state });
  };

  useEffect(
    function detectNewTouchpoint() {
      if (!touchpointContent.data?.jsonOutput) {
        setIsNewTouchpoint(true);
      }
    },
    [touchpointContent],
  );

  useEffect(
    function triggerSetInitialValues() {
      if (isNewTouchpoint) {
        handleSetInitialValues();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isNewTouchpoint],
  );

  async function handleSetInitialValues() {
    setIsSaving(true);

    try {
      const defaultFormValues = formFields.reduce(
        (acc, field) => {
          if (field.defaultValue === undefined || field.defaultValue === null) {
            acc[field.name] = "";
          } else {
            acc[field.name] = String(field.defaultValue);
          }
          return acc;
        },
        {} as Record<string, string>,
      );

      const newTouchpoint = {
        data: {
          htmlOutput: "<>",
          jsonOutput: addAnnotationIdToElements(BLANK_BEE_TEMPLATE),
        },
        sourceType: "BEE",
        ...defaultFormValues,
        ...(isTemplate && {
          redirectDestination: "no-redirect",
          redirectDestinationType: "NONE",
        }),
      };

      await saveTouchpoint(newTouchpoint);
      setIsSaved(true);
      setIsNewTouchpoint(false);
    } catch (error) {
      console.error("Error saving design", error);
      setIsSaved(false);
      // Add error handling to prevent infinite loading
      setIsNewTouchpoint(false); // Force exit from loading state on error
    }

    setIsSaving(false);
  }

  const location = useLocation();

  const lastPathSegment = location.pathname.split("/").pop();

  const currentTab = lastPathSegment as BuilderTabId;

  return (
    <Builder
      data-testid="lp-builder"
      apiValue={touchpointContent}
      contentName={currentTouchpoint.name}
      previewMode={previewMode}
      permission={Permission.PERM_TOUCHPOINT_WRITE}
      isSaved={isSaved}
      setIsSaved={setIsSaved}
      isSaving={isSaving}
    >
      <BuilderTabNav
        data-testid="lp-builder--tab-nav"
        isPreviewMode={previewMode}
        url={location.pathname}
        currentTab={currentTab}
        tabValidity={tabValidity}
        hiddenTabIds={hiddenTabIds}
      />

      <ContentWrapper data-testid="lp-builder--content-wrapper">
        <Content sx={{ backgroundColor: "background.default", pt: 3 }}>
          {isNewTouchpoint ? (
            <div className="flex flex-1 justify-center items-center">
              <LoadingSpinner />
            </div>
          ) : (
            <Routes>
              <Route
                index
                element={
                  <Navigate
                    to={previewMode ? "preview" : "design"}
                    replace
                    state={location.state}
                  />
                }
              />

              {!previewMode && (
                <Route
                  path="design"
                  element={
                    <LPBuilderDesignTab
                      onSave={saveTouchpoint}
                      setIsSaved={setIsSaved}
                      setIsSaving={setIsSaving}
                    />
                  }
                />
              )}

              {touchpointIsOther ? (
                <Route
                  path="content-settings"
                  element={
                    <Navigate
                      to={previewMode ? "../preview" : "../design"}
                      replace
                      state={location.state}
                    />
                  }
                />
              ) : (
                <Route
                  path="content-settings"
                  element={
                    <LPBuilderContentSettingsTab
                      previewMode={previewMode}
                      formFields={formFields}
                      onSave={saveTouchpoint}
                      setIsSaved={setIsSaved}
                      setIsSaving={setIsSaving}
                      isTemplate={isTemplate}
                    />
                  }
                />
              )}

              <Route path="preview" element={<LPBuilderPreviewTab previewMode={previewMode} />} />
            </Routes>
          )}
          {isLockReleaseModalOpen && <LockReleaseModal onCloseCallback={handleLockRelease} />}
        </Content>
      </ContentWrapper>
    </Builder>
  );
};

export default LPBuilder;
